/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { useState, useEffect } from 'react'
import { Link, graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'

import Layout from '~/components/layout'
import SEO from '~/components/seo'

import membershipHero from '~/assets/membershipHero.jpg'
import membershipHero2x from '~/assets/membershipHero@2x.jpg'

import Children from '~/motion/Children'
import SlideUpItem from '~/motion/SlideUpItem'

const Membership = ({ location }) => {
  const data = useStaticQuery(graphql`
    query membershipQuery {
      membership1: file(relativePath: { eq: "membership1.png" }) {
        childImageSharp {
          gatsbyImageData(width: 757)
        }
      }
      membership2: file(relativePath: { eq: "cellar@2x.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 280)
        }
      }
      membership3: file(relativePath: { eq: "membership3.png" }) {
        childImageSharp {
          gatsbyImageData(width: 517)
        }
      }
      membership4: file(relativePath: { eq: "membership4.png" }) {
        childImageSharp {
          gatsbyImageData(width: 591)
        }
      }
      site {
        siteMetadata {
          title
        }
      }
      allSanityMenus {
        nodes {
          brochure {
            title
            link
            file {
              asset {
                source {
                  _key
                  _type
                  name
                  url
                }
                url
              }
            }
          }
        }
      }
    }
  `)

  const [shouldAnimate, setShouldAnimate] = useState(false)
  const siteTitle = data.site.siteMetadata?.title || 'Title'
  const brochure = data.allSanityMenus.nodes[0].brochure.file.asset.url

  useEffect(() => {
    setTimeout(() => {
      setShouldAnimate(true)
    }, 0)
  }, [])

  return (
    <Layout location={location} title={siteTitle} pageTitle="Membership">
      <SEO
        title="Membership"
        description="Planque is a community for like-minded drinkers, a place for members to meet and share their love of wine."
      />
      <Children shouldAnimate={shouldAnimate}>
        <img
          src={`${membershipHero}`}
          srcSet={`${membershipHero} 1x, ${membershipHero2x} 2x`}
          className="membership-hero"
          sx={{ width: ['80%', '70%', '50%'] }}
          alt="Planque Membership"
        />
        <div className="page-padding" />
        <div className="page-wrap">
          <div
            sx={{
              transform: ['rotate(-90deg)', 'rotate(-90deg)', 'rotate(0deg)'],
              transformOrigin: '0 0',
              top: [150, 300, 0],
              position: 'absolute'
            }}
          >
            <SlideUpItem>
              <h1>Membership</h1>
            </SlideUpItem>
          </div>

          <div className="bpS-p100">
            <div className="ar ar-60 show-on-mobile"></div>
            <div className="ar ar-60 show-on-mobile"></div>
            <SlideUpItem>
              <p sx={{ maxWidth: 445, mt: [-20, 50] }}>
                Planque is a community for like-minded drinkers, whether
                seasoned collectors or the cellar-curious. It is a place for
                members to share in their love of wine, make meaningful
                connections, as well as store and develop their personal
                collections.
              </p>
            </SlideUpItem>
            <SlideUpItem>
              <ul className="lh-16" sx={{ mt: 30, fontSize: [18, 22] }}>
                <li sx={{ mb: 20 }}>
                  <SlideUpItem>
                    <Link
                      to="https://members.planque.co.uk/apply"
                      target="_blank"
                      className="link link-arrow-reverse"
                    >
                      Membership Application
                    </Link>
                  </SlideUpItem>
                </li>
                <li>
                  <SlideUpItem>
                    <a
                      href="/membership-brochure"
                      className="link link-arrow-reverse"
                      target="_blank"
                    >
                      Membership Brochure
                    </a>
                  </SlideUpItem>
                </li>
              </ul>
            </SlideUpItem>
          </div>
        </div>
        <div className="page-wrap" sx={{ mt: [50, 150] }}>
          <div
            className="display-flex ai-fe"
            sx={{ flexWrap: ['wrap', 'wrap', 'nowrap'] }}
          >
            <div sx={{ width: ['100%', '100%', '60%'] }}>
              <SlideUpItem>
                <GatsbyImage
                  image={data.membership1.childImageSharp.gatsbyImageData}
                  className="width-100"
                  sx={{
                    maxWidth: ['100%', '100%', 757, 757],
                    position: 'relative',
                    zIndex: 1
                  }}
                  alt="Planque Membership"
                />
              </SlideUpItem>
            </div>
            <div className="p2" sx={{ width: ['100%', '100%', '40%'] }}>
              <SlideUpItem>
                <p sx={{ maxWidth: 434, mb: [30, 100] }}>
                  The ideology is simple and fuss-free: inviting people to come
                  together to enjoy a slower, more intentional way to eat and
                  drink, fostering a deeper appreciation for food and wine
                  culture.
                </p>
              </SlideUpItem>
            </div>
          </div>
        </div>
        <div
          className="page-wrap"
          sx={{ zIndex: 'auto', my: [30, 100], maxWidth: 1200 }}
        >
          <div
            className="display-flex flex-center fd-cr bpS-fd-r rel"
            sx={{ zIndex: -1 }}
          >
            <blockquote
              className="rel"
              sx={{
                zIndex: -1,
                fontSize: [24, 33],
                lineHeight: ['26px', '39px']
              }}
            >
              Planque is home to a restaurant, members’ lounge, wine cellar, and
              retail store. Planque members have access to a host of benefits
              including in-house cellar storage, use of the club lounge and
              private dining spaces, priority access to reservations in the
              restaurant, as well as an ongoing programme of wine events.
            </blockquote>
          </div>
        </div>
        <div className="page-wrap mt3">
          <div className="" sx={{ px: [30, 30, 50] }}>
            <div>
              <ul className="fs-18 bpS-fs-24 lh-16">
                <li sx={{ mb: 20 }}>
                  <SlideUpItem>
                    <Link to="/contact" className="link link-arrow">
                      Get in Touch
                    </Link>
                  </SlideUpItem>
                </li>
                <li>
                  <SlideUpItem>
                    <Link to="/events" className="link link-arrow">
                      View Events Calendar
                    </Link>
                  </SlideUpItem>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div sx={{ marginBottom: [50, 100], position: 'relative' }}>
          <div
            sx={{
              pl: ['10vw', '15vw', '30vw'],
              display: ['none', 'none', 'block']
            }}
          >
            <SlideUpItem>
              <GatsbyImage
                image={data.membership2.childImageSharp.gatsbyImageData}
                className="width-100"
                sx={{
                  maxWidth: ['100%', '100%', 250, 250],
                  position: 'relative',
                  zIndex: 1
                }}
                alt="Planque Membership"
              />
            </SlideUpItem>
          </div>
          <div
            sx={{
              position: ['static', 'static', 'absolute'],
              width: ['80%', '100%', '50%'],
              top: 0,
              zIndex: 2,
              right: 0,
              display: 'block',
              marginTop: [30, 30, -100],
              marginBottom: [30, 30, 0]
            }}
          >
            <SlideUpItem sx={{}}>
              <GatsbyImage
                image={data.membership3.childImageSharp.gatsbyImageData}
                className="width-100"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: 600,
                  marginLeft: 'auto',
                  marginRight: 100
                  // border: '10px solid red'
                }}
                alt="Planque Membership"
              />
            </SlideUpItem>
          </div>
          <SlideUpItem sx={{ zIndex: 1, width: '100%' }}>
            <GatsbyImage
              image={data.membership4.childImageSharp.gatsbyImageData}
              className="width-100"
              sx={{
                maxWidth: ['70%', '100%', 708, 708],
                position: 'relative',
                zIndex: 1,
                marginLeft: ['auto', 'auto', 100]
              }}
              alt="Planque Membership"
            />
          </SlideUpItem>
        </div>
      </Children>
    </Layout>
  )
}

export default Membership
